import React, { Component } from "react";
import { connect } from "react-redux";
import {
  getEmailLogs,
  retryEmailLog,
  retryBulkEmailLogs,
  syncEmailLogMailgun,
} from "../../../services/admin";
import Pagination from "../../../components/tablePagination";
import { toast } from "react-toastify";
import EmailLogDetail from "./emailLogDetail";
import { STATUS_STYLES, RETRYABLE_STATUSES, StatusBadge, formatInTimezone, truncate } from "./helpers";

var EMPTY_STATS = { total: 0, delivered: 0, failed: 0, pending_or_accepted: 0, delivery_rate: 0, today_total: 0, today_failed: 0 };
var EMPTY_FILTERS = { status: "", appointment_id: "", user_id: "", search: "", date_from: "", date_to: "" };

function StatBox(props) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        padding: "8px 18px",
        minWidth: 90,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: props.color }}>{props.value}</div>
      <div style={{ fontSize: 12, color: "#777" }}>{props.label}</div>
    </div>
  );
}

// Compact row buttons. Inline so they beat the global `button { height: 35px }`
// reset in public/assets/css/demo.css, which btn-sm does not override.
var ACTION_BUTTON_STYLE = { height: 24, padding: "0 8px", fontSize: 11, lineHeight: "22px", whiteSpace: "nowrap" };

function isRetryable(log) {
  return RETRYABLE_STATUSES.indexOf(log.status) !== -1;
}

class EmailLogs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      stats: EMPTY_STATS,
      totalCount: 0,
      page: 1,
      rowsPerPage: 10,
      retryingIds: [],
      syncingIds: [],
      selectedIds: [],
      bulkRetrying: false,
      loading: false,
      detailLogId: null,
      detailRefreshKey: 0,
      filters: Object.assign({}, EMPTY_FILTERS),
    };
  }

  componentDidMount() {
    this.fetchLogs(1, 10, this.state.filters);
  }

  fetchLogs(currentPage, currentRows, currentFilters) {
    var params = Object.assign({ page: currentPage, per_page: currentRows }, currentFilters);

    this.setState({ loading: true });

    getEmailLogs(params)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          var logData = res.data.data.logs;
          this.setState({
            logs: logData.data || [],
            totalCount: logData.total || 0,
            stats: res.data.data.stats || EMPTY_STATS,
            selectedIds: [],
          });
        } else if (res && res.data && res.data.message) {
          toast.error(res.data.message);
        }
      }.bind(this))
      .catch(function() {})
      .finally(function() {
        this.setState({ loading: false });
      }.bind(this));
  }

  refresh() {
    this.fetchLogs(this.state.page, this.state.rowsPerPage, this.state.filters);
    this.setState(function(prev) {
      return { detailRefreshKey: prev.detailRefreshKey + 1 };
    });
  }

  handleFilterChange(e) {
    var name = e.target.name;
    var value = e.target.value;
    this.setState(function(prev) {
      var updated = Object.assign({}, prev.filters);
      updated[name] = value;
      return { filters: updated };
    });
  }

  applyFilters() {
    this.setState({ page: 1 }, function() {
      this.fetchLogs(1, this.state.rowsPerPage, this.state.filters);
    }.bind(this));
  }

  resetFilters() {
    var empty = Object.assign({}, EMPTY_FILTERS);
    this.setState({ filters: empty, page: 1 }, function() {
      this.fetchLogs(1, this.state.rowsPerPage, empty);
    }.bind(this));
  }

  getPaginationData(data) {
    this.setState({ page: data.page, rowsPerPage: data.rowsPerPage }, function() {
      this.fetchLogs(data.page, data.rowsPerPage, this.state.filters);
      if (this.tableRef) {
        this.tableRef.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }.bind(this));
  }

  toggleSelected(id) {
    this.setState(function(prev) {
      var selected = prev.selectedIds.indexOf(id) !== -1
        ? prev.selectedIds.filter(function(i) { return i !== id; })
        : prev.selectedIds.concat([id]);
      return { selectedIds: selected };
    });
  }

  toggleSelectAll(retryableIds) {
    this.setState(function(prev) {
      var allSelected = retryableIds.length > 0 && retryableIds.every(function(id) { return prev.selectedIds.indexOf(id) !== -1; });
      return { selectedIds: allSelected ? [] : retryableIds };
    });
  }

  handleRetry(id) {
    this.setState(function(prev) {
      return { retryingIds: prev.retryingIds.concat([id]) };
    });

    retryEmailLog(id)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          toast.success(res.data.message || "Email retry queued successfully");
          this.refresh();
        } else {
          toast.error((res && res.data && res.data.message) || "Retry failed");
        }
      }.bind(this))
      .catch(function() { toast.error("Retry request failed"); })
      .finally(function() {
        this.setState(function(prev) {
          return { retryingIds: prev.retryingIds.filter(function(i) { return i !== id; }) };
        });
      }.bind(this));
  }

  // ids: [] retries every failed/bounced email; otherwise only the given ones.
  handleRetryBulk(ids) {
    var message = ids.length > 0
      ? "Re-send " + ids.length + " selected email(s)?"
      : "Re-send ALL failed and bounced emails (not just the ones matching the current filters)?";
    if (!window.confirm(message)) {
      return;
    }

    this.setState({ bulkRetrying: true });

    retryBulkEmailLogs(ids)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          toast.success(res.data.message || "Bulk retry queued");
          this.refresh();
        } else {
          toast.error((res && res.data && res.data.message) || "Bulk retry failed");
        }
      }.bind(this))
      .catch(function() { toast.error("Bulk retry request failed"); })
      .finally(function() {
        this.setState({ bulkRetrying: false });
      }.bind(this));
  }

  handleSync(id) {
    this.setState(function(prev) {
      return { syncingIds: prev.syncingIds.concat([id]) };
    });

    syncEmailLogMailgun(id)
      .then(function(res) {
        if (res && res.data && res.data.status) {
          toast.success(res.data.message || "Mailgun status synced");
          this.refresh();
        } else {
          var error = res && res.data ? (typeof res.data.error === "string" && res.data.error) || res.data.message : null;
          toast.error(error || "Mailgun sync failed");
        }
      }.bind(this))
      .catch(function() { toast.error("Mailgun sync request failed"); })
      .finally(function() {
        this.setState(function(prev) {
          return { syncingIds: prev.syncingIds.filter(function(i) { return i !== id; }) };
        });
      }.bind(this));
  }

  render() {
    var logs = this.state.logs;
    var stats = this.state.stats;
    var filters = this.state.filters;
    var retryingIds = this.state.retryingIds;
    var syncingIds = this.state.syncingIds;
    var selectedIds = this.state.selectedIds;
    var bulkRetrying = this.state.bulkRetrying;
    var loading = this.state.loading;
    var userTz = this.props.adminTimezone || null;
    var retryableIds = logs.filter(isRetryable).map(function(log) { return log.id; });
    var allSelected = retryableIds.length > 0 && retryableIds.every(function(id) { return selectedIds.indexOf(id) !== -1; });
    var detailLogId = this.state.detailLogId;

    return (
      <div>
        {/* Stats bar */}
        <div className="d-flex" style={{ gap: 16, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <StatBox label="Total" value={stats.total} color="#555" />
          <StatBox label="Delivered" value={stats.delivered} color="#2e7d32" />
          <StatBox label="Failed / Bounced" value={stats.failed} color="#c62828" />
          <StatBox label="In Flight" value={stats.pending_or_accepted} color="#1565c0" />
          <StatBox label="Delivery Rate" value={(stats.delivery_rate || 0) + "%"} color="#555" />
          <StatBox label="Today (failed)" value={stats.today_total + " (" + stats.today_failed + ")"} color="#555" />

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {selectedIds.length > 0 && (
              <button className="btn btn-warning" onClick={this.handleRetryBulk.bind(this, selectedIds)} disabled={bulkRetrying}>
                {bulkRetrying ? "Queuing..." : "Retry Selected (" + selectedIds.length + ")"}
              </button>
            )}
            {stats.failed > 0 && (
              <button className="btn btn-danger" onClick={this.handleRetryBulk.bind(this, [])} disabled={bulkRetrying}>
                {bulkRetrying ? "Queuing..." : "Retry All Failed"}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="d-flex" style={{ gap: 10, flexWrap: "wrap", marginBottom: 16, alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: 12 }}>Search</label>
            <input
              name="search"
              value={filters.search}
              onChange={this.handleFilterChange.bind(this)}
              onKeyDown={function(e) { if (e.key === "Enter") { this.applyFilters(); } }.bind(this)}
              className="form-control"
              type="text"
              placeholder="Recipient / subject / type"
              style={{ minWidth: 220 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>Status</label>
            <select name="status" value={filters.status} onChange={this.handleFilterChange.bind(this)} className="form-control" style={{ minWidth: 130 }}>
              <option value="">All</option>
              {Object.keys(STATUS_STYLES).map(function(status) {
                return <option key={status} value={status}>{STATUS_STYLES[status].label}</option>;
              })}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12 }}>Appointment ID</label>
            <input
              name="appointment_id"
              value={filters.appointment_id}
              onChange={this.handleFilterChange.bind(this)}
              className="form-control"
              type="number"
              placeholder="Appointment ID"
              style={{ minWidth: 130 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>User ID</label>
            <input
              name="user_id"
              value={filters.user_id}
              onChange={this.handleFilterChange.bind(this)}
              className="form-control"
              type="number"
              placeholder="User ID"
              style={{ minWidth: 110 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>From</label>
            <input name="date_from" value={filters.date_from} onChange={this.handleFilterChange.bind(this)} className="form-control" type="date" />
          </div>
          <div>
            <label style={{ fontSize: 12 }}>To</label>
            <input name="date_to" value={filters.date_to} onChange={this.handleFilterChange.bind(this)} className="form-control" type="date" />
          </div>
          <button className="btn btn-primary" onClick={this.applyFilters.bind(this)}>Apply</button>
          <button className="btn btn-secondary" onClick={this.resetFilters.bind(this)}>Reset</button>
        </div>

        {/* Table */}
        <div className="table-box-medium" ref={function(el) { this.tableRef = el; }.bind(this)}>
          <table className="caseTable">
            <tbody>
              <tr className="dark">
                <th>
                  <input
                    type="checkbox"
                    title="Select all failed/bounced on this page"
                    checked={allSelected}
                    disabled={retryableIds.length === 0}
                    onChange={this.toggleSelectAll.bind(this, retryableIds)}
                  />
                </th>
                <th>#</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Delivery</th>
                <th>Retries</th>
                <th>Sent At</th>
                <th>Action</th>
              </tr>

              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    <div className="custom-loader">
                      <div className="loader-dot"></div>
                      <div className="loader-dot"></div>
                      <div className="loader-dot"></div>
                    </div>
                    <div className="loader-text mt-2">Loading logs...</div>
                  </td>
                </tr>
              ) : logs.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 20 }}>
                    No records found
                  </td>
                </tr>
              )}

              {!loading && logs.map(function(log) {
                var retryable = isRetryable(log);
                var isRetrying = retryingIds.indexOf(log.id) !== -1;
                var isSyncing = syncingIds.indexOf(log.id) !== -1;
                // Error from our side (transport/retry) wins over Mailgun's delivery response.
                var delivery = log.error_message
                  || (log.delivery_code ? log.delivery_code + " " + (log.delivery_description || log.delivery_message || "") : log.delivery_message);

                return (
                  <tr key={log.id}>
                    <td>
                      {retryable && (
                        <input
                          type="checkbox"
                          checked={selectedIds.indexOf(log.id) !== -1}
                          onChange={this.toggleSelected.bind(this, log.id)}
                        />
                      )}
                    </td>
                    <td>{log.id}</td>
                    <td style={{ fontSize: 12, maxWidth: 200, wordBreak: "break-all" }}>
                      {log.recipient_name && <div style={{ fontWeight: 600 }}>{log.recipient_name}</div>}
                      {log.recipient_email}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 220 }} title={log.subject || ""}>{truncate(log.subject, 60)}</td>
                    <td><StatusBadge status={log.status} /></td>
                    <td style={{ fontSize: 12, maxWidth: 200 }} title={delivery || ""}>{truncate(delivery, 60)}</td>
                    <td>{log.retry_count}</td>
                    <td style={{ fontSize: 12 }}>{formatInTimezone(log.sent_at || log.created_at, userTz)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button style={ACTION_BUTTON_STYLE} className="btn btn-sm btn-secondary" onClick={function() { this.setState({ detailLogId: log.id }); }.bind(this)}>
                          View
                        </button>
                        {retryable && (
                          <button style={ACTION_BUTTON_STYLE} className="btn btn-sm btn-warning" onClick={this.handleRetry.bind(this, log.id)} disabled={isRetrying}>
                            {isRetrying ? "..." : "Retry"}
                          </button>
                        )}
                        {log.message_id && (
                          <button style={ACTION_BUTTON_STYLE} className="btn btn-sm btn-primary" onClick={this.handleSync.bind(this, log.id)} disabled={isSyncing} title="Refresh status from Mailgun">
                            {isSyncing ? "..." : "Sync"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }.bind(this))}
            </tbody>
          </table>
        </div>

        <Pagination
          rowsPerPageOptions={[10, 20, 50]}
          count={this.state.totalCount}
          controlled={true}
          page={this.state.page}
          rowsPerPage={this.state.rowsPerPage}
          getPaginationData={this.getPaginationData.bind(this)}
        />

        {detailLogId && (
          <EmailLogDetail
            logId={detailLogId}
            refreshKey={this.state.detailRefreshKey}
            adminTimezone={userTz}
            isRetrying={retryingIds.indexOf(detailLogId) !== -1}
            isSyncing={syncingIds.indexOf(detailLogId) !== -1}
            onRetry={this.handleRetry.bind(this)}
            onSync={this.handleSync.bind(this)}
            onClose={function() { this.setState({ detailLogId: null }); }.bind(this)}
          />
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    adminTimezone: state.user && state.user.user ? state.user.user.timezone : null,
  };
}

export default connect(mapStateToProps)(EmailLogs);
